import { useLocation } from 'react-router-dom'
import { useMemo } from 'react'

/**
 * Custom hook to extract and sanitize the utm_headline query parameter from the URL.
 *
 * This hook:
 * - Extracts utm_headline from URL query parameters
 * - Decodes URL-encoded strings (%20 → space)
 * - Replaces + with spaces (+ → space)
 * - Trims whitespace
 * - Handles decode errors gracefully
 * - Returns null if the parameter is missing or empty after processing
 *
 * @returns {string|null} The sanitized utm_headline value, or null if not present/empty
 *
 * @example
 * // URL: /page?utm_headline=Buy%20Gift%20Cards
 * const utmHeadline = useUtmHeadline()
 * console.log(utmHeadline) // "Buy Gift Cards"
 *
 * @example
 * // URL: /page?utm_headline=Corporate+Rewards
 * const utmHeadline = useUtmHeadline()
 * console.log(utmHeadline) // "Corporate Rewards"
 *
 * @example
 * // URL: /page (no utm_headline parameter)
 * const utmHeadline = useUtmHeadline()
 * console.log(utmHeadline) // null
 */
export const useUtmHeadline = () => {
    const location = useLocation()

    return useMemo(() => {
        const params = new URLSearchParams(location.search)
        const raw = params.get('utm_headline')

        if (!raw) return null

        let value = raw

        // Try to decode URL-encoded characters
        try {
            value = decodeURIComponent(value)
        } catch (e) {
            // If decode fails, use the raw value
            // This handles malformed URL encoding gracefully
        }

        // Replace + signs with spaces (common in URL encoding)
        value = value.replace(/\+/g, ' ').trim()

        // Return null if the value is empty after processing
        return value.length > 0 ? value : null
    }, [location.search])
}
